import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useFilterDropdown } from '../../../hooks/useFilterDropdown';
import FilterButton from './FilterButton';
import FilterContent from './FilterContent';

const FilterDropdown = ({ 
  config, 
  values, 
  onChange, 
  onClearFilters,
  className = '',
  buttonProps = {},
  contentProps = {}
}) => {
  const { isMobile } = useTheme();
  const { 
    isOpen, 
    position, 
    buttonRef, 
    dropdownRef, 
    toggle, 
    close 
  } = useFilterDropdown();

  // Close on click outside
  const clickOutsideRef = useClickOutside(close);

  // Get active filters count
  const getActiveFiltersCount = () => {
    return Object.values(values).filter(value => value && value !== '').length;
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return getActiveFiltersCount() > 0;
  };

  // Handle clear individual filter
  const handleClearFilter = (key) => {
    onChange(key, '');
  };

  // Handle clear all filters
  const handleClearAll = () => {
    onClearFilters();
    // Don't close dropdown after clearing - user might want to set new filters
  };

  const activeCount = getActiveFiltersCount();
  const hasFilters = hasActiveFilters();

  return (
    <div className={`relative ${className}`} ref={clickOutsideRef}>
      {/* Filter Button */}
      <div ref={buttonRef}>
        <FilterButton
          activeCount={activeCount}
          hasActiveFilters={hasFilters}
          onClick={toggle}
          {...buttonProps}
        />
      </div>

      {/* Dropdown Content */}
      {isOpen && (
        <>
          {/* Mobile Backdrop */}
          {isMobile && (
            <div className="fixed inset-0 bg-black bg-opacity-25 z-40" />
          )}

          {/* Dropdown Container */}
          <div
            ref={dropdownRef}
            className={`
              absolute z-50 transform transition-all duration-200 ease-out
              ${isMobile 
                ? 'fixed inset-x-4 top-20 w-auto' // Full width on mobile with margin
                : position === 'top' 
                  ? 'bottom-full mb-2 right-0' 
                  : 'top-full mt-2 right-0'
              }
              ${position === 'top' ? 'origin-bottom-right' : 'origin-top-right'}
              ${!isMobile ? 'w-72 min-w-72 max-w-80' : ''}
              ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}
            style={{
              // Ensure dropdown doesn't go off-screen on desktop
              ...(isMobile ? {} : {
                maxWidth: '85vw', // Reduced from 90vw
                minWidth: '288px'  // Reduced from 320px
              })
            }}
          >
            <FilterContent
              config={config}
              values={values}
              onChange={onChange}
              onClear={handleClearFilter}
              onClearAll={handleClearAll}
              layout={isMobile ? 'stack' : 'grid'}
              {...contentProps}
            />
          </div>
        </>
      )}

      {/* Screen Reader Only Text */}
      <div className="sr-only">
        {isOpen ? 'Filter dropdown is open' : 'Filter dropdown is closed'}
        {activeCount > 0 && `, ${activeCount} filters active`}
      </div>
    </div>
  );
};

export default FilterDropdown;